import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { TrendingUp, TrendingDown, DollarSign, Landmark, UserPlus } from 'lucide-react';
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: 7,  label: '7d'  },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

const fmtUsd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '';
  const parts = String(d).split('-');
  if (parts.length !== 3) return d;
  return `${parts[1]}/${parts[2]}`;
};

const OverlayToggle = ({ checked, onChange, icon: Icon, label, color, testId }) => (
  <label className="flex items-center gap-2 text-sm text-muted-foreground/80 cursor-pointer select-none">
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    <Icon className={`h-3.5 w-3.5 ${color}`} />
    <span>{label}</span>
  </label>
);

export default function RevenueTrendChart() {
  const [days, setDays] = useState(30);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlaid, setShowPlaid] = useState(true);
  const [showSignups, setShowSignups] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/owner-admin/dashboard/metrics-trend?days=${days}`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setPoints(Array.isArray(data.points) ? data.points : []);
        }
      } catch (e) {
        console.error('metrics-trend fetch failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const summary = useMemo(() => {
    if (!points.length) return { start: 0, end: 0, delta: 0, pct: 0, hasHistory: false };
    const start = Number(points[0]?.mrr_usd || 0);
    const end   = Number(points[points.length - 1]?.mrr_usd || 0);
    const delta = end - start;
    const pct   = start > 0 ? (delta / start) * 100 : (end > 0 ? 100 : 0);
    return { start, end, delta, pct, hasHistory: points.length > 1 };
  }, [points]);

  // Unit economics summary — MRR vs Plaid floor + total signups over period.
  const econ = useMemo(() => {
    if (!points.length) return { totalSignups: 0, latestPlaid: 0, marginPct: 0 };
    const totalSignups = points.reduce((sum, p) => sum + Number(p.signups_24h || 0), 0);
    const latestPlaid = Number(points[points.length - 1]?.plaid_floor_usd || 0);
    const latestMrr = Number(points[points.length - 1]?.mrr_usd || 0);
    const marginPct = latestMrr > 0 ? ((latestMrr - latestPlaid) / latestMrr) * 100 : 0;
    return { totalSignups, latestPlaid, marginPct };
  }, [points]);

  const trendUp = summary.delta >= 0;

  return (
    <Card className="bg-card border-white/15" data-testid="revenue-trend-chart">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-white">Revenue Trend</CardTitle>
            <CardDescription className="text-muted-foreground">
              Nightly MRR snapshots — watch it climb month over month
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                size="sm"
                variant={days === r.key ? 'secondary' : 'outline'}
                onClick={() => setDays(r.key)}
                className={
                  days === r.key
                    ? 'bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500'
                    : 'border-white/20 text-muted-foreground/70 hover:bg-white/10'
                }
                data-testid={`revenue-trend-range-${r.key}`}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <p className="text-xs text-muted-foreground">Current MRR</p>
            <p className="text-2xl font-semibold text-white mt-1" data-testid="revenue-trend-current">
              {fmtUsd(summary.end)}
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <p className="text-xs text-muted-foreground">Start of period</p>
            <p className="text-2xl font-semibold text-white mt-1">
              {fmtUsd(summary.start)}
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <p className="text-xs text-muted-foreground">Change</p>
            <div className={`flex items-center gap-1 mt-1 text-2xl font-semibold ${trendUp ? 'text-emerald-400' : 'text-red-400'}`}>
              {trendUp ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
              <span data-testid="revenue-trend-delta">
                {trendUp ? '+' : ''}{fmtUsd(summary.delta)}
              </span>
            </div>
            {summary.hasHistory && summary.start > 0 && (
              <span className="text-xs opacity-70 text-muted-foreground">
                {trendUp ? '+' : ''}{summary.pct.toFixed(1)}% vs start
              </span>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <UserPlus className="h-3 w-3 text-sky-400" /> Signups ({days}d)
            </p>
            <p className="text-2xl font-semibold text-sky-300 mt-1" data-testid="revenue-trend-signups">
              {econ.totalSignups}
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Landmark className="h-3 w-3 text-amber-400" /> MRR margin
            </p>
            <p className="text-2xl font-semibold text-amber-300 mt-1" data-testid="revenue-trend-margin">
              {econ.marginPct.toFixed(0)}%
            </p>
            <p className="text-[11px] text-muted-foreground">
              vs Plaid ${econ.latestPlaid.toFixed(2)}/mo
            </p>
          </div>
        </div>

        {/* Overlay toggles */}
        <div className="flex flex-wrap items-center gap-6 mb-3">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Overlays</span>
          <OverlayToggle
            checked={showPlaid}
            onChange={setShowPlaid}
            icon={Landmark}
            label="Plaid floor cost"
            color="text-amber-400"
            testId="overlay-plaid"
          />
          <OverlayToggle
            checked={showSignups}
            onChange={setShowSignups}
            icon={UserPlus}
            label="Signups / day"
            color="text-sky-400"
            testId="overlay-signups"
          />
        </div>

        {/* Chart */}
        <div className="h-72 w-full">
          {loading ? (
            <Skeleton className="h-full w-full bg-white/10" />
          ) : points.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No MRR snapshots yet — the nightly job captures the first one at 07:00 UTC.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#10b981" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDate}
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={20}
                />
                <YAxis
                  yAxisId="usd"
                  tickFormatter={(v) => `$${Math.round(v)}`}
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={54}
                />
                <YAxis
                  yAxisId="signups"
                  orientation="right"
                  tickFormatter={(v) => Math.round(v)}
                  stroke="rgba(56,189,248,0.55)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={30}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#0b1220',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 8,
                    color: '#e2e8f0',
                    fontSize: 12,
                  }}
                  labelFormatter={(v) => `Day: ${v}`}
                  formatter={(value, name) => {
                    if (name === 'Signups') return [value, 'Signups'];
                    return [fmtUsd(value), name];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: '#e2e8f0' }}
                  iconType="circle"
                />
                {showSignups && (
                  <Bar
                    yAxisId="signups"
                    dataKey="signups_24h"
                    name="Signups"
                    fill="#38bdf8"
                    fillOpacity={0.55}
                    barSize={days <= 7 ? 20 : days <= 30 ? 8 : 3}
                    radius={[3, 3, 0, 0]}
                  />
                )}
                <Area
                  yAxisId="usd"
                  type="monotone"
                  dataKey="mrr_usd"
                  name="MRR"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#mrrGrad)"
                />
                {showPlaid && (
                  <Line
                    yAxisId="usd"
                    type="monotone"
                    dataKey="plaid_floor_usd"
                    name="Plaid floor"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
